import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { applyRosterOp, type PendingRosterOp, type RosterSlot } from './pendingRosterOps'
import { BuildingStatus, initialLandState, LandState, RepairRequirements, AllMiningStatus, EmpirePeriodInfo } from './state'
import type { KingdomTechEffects } from '../../../constants/kingdom/kingdomTechEffects'
import { BUILDING_DEFAULT_HP } from '../../../constants/land'

export const landSlice = createSlice({
  name: 'land',
  initialState: initialLandState as LandState,
  reducers: {
    setLandLoading: (state: LandState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setImmediateLoading: (state: LandState, action: PayloadAction<boolean>) => {
      state.isImmediate = action.payload
    },
    setLandError: (state: LandState, action: PayloadAction<string | undefined>) => {
      state.error = action.payload
    },
    /**
     * Main land: repair just started — set repair end time so repair animation shows immediately.
     * HP stays damaged until the server confirms (fetchLandStatus after API).
     */
    patchBuildingRepairStarted: (
      state: LandState,
      action: PayloadAction<{ building: keyof BuildingStatus; repairDurationSeconds: number }>,
    ) => {
      const { building, repairDurationSeconds } = action.payload
      const b = state.buildingStatus[building] as any
      if (!b) return
      const sec = Math.max(1, Math.floor(Number(repairDurationSeconds) || 300))
      b.repairCooldown = new Date(Date.now() + sec * 1000)
    },
    /** Main land: instant repair / cooldown skip — full HP and no repair timer (refetch reconciles). */
    patchBuildingInstantRepairComplete: (state: LandState, action: PayloadAction<{ building: keyof BuildingStatus }>) => {
      const { building } = action.payload
      const b = state.buildingStatus[building] as any
      if (!b || typeof b.maxHp !== 'number') return
      const max = b.maxHp
      b.hp = max
      b.repairCooldown = null
      if (building === 'wall' && typeof b.effectiveHp === 'number') {
        b.effectiveHp = max
      }
    },
    patchBuildingClearUpgradeCooldown: (state: LandState, action: PayloadAction<{ building: keyof BuildingStatus }>) => {
      const b = state.buildingStatus[action.payload.building] as any
      if (b) b.upgradeCooldown = null
    },
    setLandStatus: (state: LandState, action: PayloadAction<BuildingStatus>) => {
      // Merge with existing state to ensure all buildings are present
      // This prevents undefined errors when backend doesn't return all buildings
      const existingStatus = state.buildingStatus || initialLandState.buildingStatus
      const payload = action.payload
      
      // Create a new buildingStatus object that merges existing and new data
      const mergedStatus = { ...existingStatus } as BuildingStatus
      
      // Update buildings that exist in the payload
      for (const key in payload) {
        if (key in existingStatus) {
          // Use type assertion to handle different building structures (wall has effectiveHp, others don't)
          const existingBuilding = existingStatus[key as keyof BuildingStatus]
          const payloadBuilding = payload[key as keyof BuildingStatus]
          mergedStatus[key as keyof BuildingStatus] = {
            ...existingBuilding,
            ...payloadBuilding,
          } as any
        } else {
          // If a new building is added, include it
          mergedStatus[key as keyof BuildingStatus] = payload[key as keyof BuildingStatus] as any
        }
      }
      
      state.buildingStatus = mergedStatus
    },
    setCastleBuildingStatus: (state: LandState, action: PayloadAction<{ castleId: number; buildingStatus: BuildingStatus }>) => {
      // Store castle building status separately from main land
      const { castleId, buildingStatus } = action.payload
      
      // Start with initial state to ensure all buildings have default values
      const baseStatus = { ...initialLandState.buildingStatus } as BuildingStatus
      const payload = buildingStatus
      
      // Create a new buildingStatus object that merges initial defaults with new data
      const mergedStatus = { ...baseStatus } as BuildingStatus
      
      // Update buildings that exist in the payload, ensuring all required fields are present
      for (const key in payload) {
        if (payload[key as keyof BuildingStatus]) {
          const payloadBuilding = payload[key as keyof BuildingStatus] as any
          const baseBuilding = baseStatus[key as keyof BuildingStatus] as any
          
          // Ensure HP values are numbers, not undefined or null
          const hp = typeof payloadBuilding?.hp === 'number' ? payloadBuilding.hp : (baseBuilding?.hp ?? 0)
          const maxHp = typeof payloadBuilding?.maxHp === 'number' ? payloadBuilding.maxHp : (baseBuilding?.maxHp ?? 100)
          const level = typeof payloadBuilding?.level === 'number' ? payloadBuilding.level : (baseBuilding?.level ?? 0)
          
          // Merge with defaults to ensure all fields exist
          mergedStatus[key as keyof BuildingStatus] = {
            level: level,
            hp: hp,
            maxHp: maxHp,
            characters: payloadBuilding?.characters ?? baseBuilding?.characters ?? null,
            // For wall, include effectiveHp
            ...(key === 'wall' && { 
              effectiveHp: typeof payloadBuilding?.effectiveHp === 'number' 
                ? payloadBuilding.effectiveHp 
                : (baseBuilding?.effectiveHp ?? 0) 
            })
          } as any
        }
      }
      
      state.castleBuildingStatus[castleId] = mergedStatus
    },
    setRepairRequirements: (state: LandState, action: PayloadAction<RepairRequirements>) => {
      state.repairRequirements = action.payload
    },
    setRestoreBuilding: (state: LandState) => {
      state.isRestored = true
    },
    setFetchSupportCharacters: (state: LandState, action: PayloadAction<any[]>) => {
      state.supportCharacters = action.payload
    },
    setSupportCharacters: (state: LandState, action: PayloadAction<any[]>) => {
      state.supportCharacters = [...state.supportCharacters, ...action.payload]
      state.isSetSupportCharacterSuccess = true
    },
    setClearStatus: (state: LandState) => {
      state.isSetSupportCharacterSuccess = false
      state.error = undefined
      state.isLoading = false
    },
    setSupportState: (state: LandState, action: PayloadAction<boolean>) => {
      state.supportState = action.payload
    },
    setUpgradeRequirements: (state: LandState, action: PayloadAction<any>) => {
      state.upgradeRequirements = action.payload
    },
    setUpgradeBuilding: (state: LandState, action: PayloadAction<any>) => {
      const building = action.payload as keyof BuildingStatus
      const buildingStatus = state.buildingStatus[building]
      
      if (buildingStatus) {
        const newLevel = buildingStatus.level + 1
        buildingStatus.level = newLevel
        
        // Update maxHp and hp based on the new level
        const buildingHpConfig = BUILDING_DEFAULT_HP[building as keyof typeof BUILDING_DEFAULT_HP]
        if (buildingHpConfig) {
          const newMaxHp = buildingHpConfig[`level${newLevel}` as keyof typeof buildingHpConfig]
          if (newMaxHp !== undefined) {
            buildingStatus.maxHp = newMaxHp
            buildingStatus.hp = newMaxHp // When upgrading, HP is restored to max
          }
        }
      }
    },
    setUpgradeAvailable: (state: LandState, action: PayloadAction<boolean>) => {
      state.upgradeAvailable = action.payload
    },
    setRemoveCharacterFromBuilding: (state: LandState, action: PayloadAction<any>) => {
      state.supportCharacters = state.supportCharacters.filter((character: any) => character._id !== action.payload.character)
    },
    setAddCharacterToBuilding: (state: LandState, action: PayloadAction<any>) => {
      state.supportCharacters = [...state.supportCharacters, action.payload.character]
    },
    setWellStart: (state: LandState, action: PayloadAction<any>) => {
    },
    setWellMiningStarted: (state: LandState, action: PayloadAction<number>) => {
      const wellIndex = action.payload
      const key = wellIndex === 1 ? 'well1' : 'well2'
      if (!state.allMiningStatus) state.allMiningStatus = {}
      state.allMiningStatus[key] = { isStartMining: true, isCompletedMining: false }
    },
    setWellCheck: (state: LandState, action: PayloadAction<any>) => {
    },
    setTowerMiningStart: (state: LandState, action: PayloadAction<any>) => {
    },
    setTowerMiningCheck: (state: LandState, action: PayloadAction<any>) => {
    },
    setTowerMiningClaim: (state: LandState, action: PayloadAction<any>) => {
    },
    setTowerMiningStop: (state: LandState, action: PayloadAction<any>) => {
    },
    setTrainingCheck: (state: LandState, action: PayloadAction<any>) => {
    },
    setTrainingClaim: (state: LandState, action: PayloadAction<any>) => {
    },
    setTrainingStart: (state: LandState, action: PayloadAction<any>) => {
    },
    setImmediateTraining: (state: LandState, action: PayloadAction<any>) => {
    },
    setHealCharacter: (state: LandState, action: PayloadAction<any>) => {
    },
    setAllMiningStatus: (state: LandState, action: PayloadAction<AllMiningStatus>) => {
      state.allMiningStatus = action.payload
    },
    /** Shallow-merge mining rows so UI can update before HTTP completes; server refetch reconciles. */
    patchAllMiningStatus: (state: LandState, action: PayloadAction<Partial<AllMiningStatus>>) => {
      if (!state.allMiningStatus) state.allMiningStatus = {}
      const patch = action.payload as Record<string, Record<string, unknown>>
      for (const key of Object.keys(patch)) {
        const next = patch[key]
        if (next && typeof next === 'object' && !Array.isArray(next)) {
          const prev = (state.allMiningStatus as Record<string, Record<string, unknown>>)[key] || {}
          ;(state.allMiningStatus as Record<string, Record<string, unknown>>)[key] = { ...prev, ...next }
        }
      }
    },
    /**
     * Put one `allMiningStatus` row back exactly as it was before an optimistic patch.
     * `value: null` means the row did not exist yet, so the rollback deletes it rather than
     * leaving a half-written `{}` the mining UI would read as "idle but known".
     */
    restoreAllMiningStatusEntry: (
      state: LandState,
      action: PayloadAction<{ key: string; value: Record<string, unknown> | null }>,
    ) => {
      if (!state.allMiningStatus) state.allMiningStatus = {}
      const all = state.allMiningStatus as Record<string, unknown>
      const { key, value } = action.payload
      if (value === null) {
        delete all[key]
        return
      }
      all[key] = { ...value }
    },
    /**
     * UI-first edit to one building entry. `castleId` 0 targets main land; 5+ targets the generic
     * `castleBuildingStatus` map (castles 1-4 keep their own slices and are patched there).
     */
    patchBuildingStatusEntry: (
      state: LandState,
      action: PayloadAction<{ castleId: number; building: string; patch: Record<string, unknown> }>,
    ) => {
      const { castleId, building, patch } = action.payload
      const bucket = castleId > 0 ? state.castleBuildingStatus?.[castleId] : state.buildingStatus
      if (!bucket) return
      const entry = (bucket as unknown as Record<string, Record<string, unknown> | undefined>)[building]
      if (!entry) return
      Object.assign(entry, patch)
    },
    /* ---------------------------------------------------------------- */
    /* Pending roster intent                                            */
    /* ---------------------------------------------------------------- */
    /** Record the player's intent. The roster selector replays it over server state immediately. */
    enqueueRosterOp: (state: LandState, action: PayloadAction<PendingRosterOp>) => {
      state.pendingRosterOps.push(action.payload)
    },
    /**
     * The server acknowledged the operation. Its effect is folded into the stored roster and the
     * op is dropped in the same action, so the displayed roster never changes across the two.
     * Nothing is read out of the response — a success means "it committed", not "here is the
     * state", which is what stopped a just-assigned character from vanishing when an earlier
     * remove was acknowledged.
     */
    confirmRosterOp: (state: LandState, action: PayloadAction<{ id: string }>) => {
      const index = state.pendingRosterOps.findIndex((op) => op.id === action.payload.id)
      if (index < 0) return
      const [op] = state.pendingRosterOps.splice(index, 1)
      const bucket = op.castleId > 0 ? state.castleBuildingStatus?.[op.castleId] : state.buildingStatus
      const entry = (bucket as unknown as Record<string, { characters?: unknown[] } | undefined>)?.[op.building]
      if (!entry) return
      entry.characters = applyRosterOp((entry.characters ?? []) as RosterSlot[], op)
    },
    /** The server rejected it. Dropping the op un-replays its effect; server state is untouched. */
    failRosterOp: (state: LandState, action: PayloadAction<{ id: string }>) => {
      state.pendingRosterOps = state.pendingRosterOps.filter((op) => op.id !== action.payload.id)
    },
    setEmpirePeriod: (state: LandState, action: PayloadAction<EmpirePeriodInfo>) => {
      state.empirePeriod = action.payload
    },
    setKingdomTechEffects: (state: LandState, action: PayloadAction<KingdomTechEffects>) => {
      state.kingdomTechEffects = action.payload
    },
  }
})

export const {
  setLandLoading,
  setImmediateLoading,
  setLandError,
  patchBuildingRepairStarted,
  patchBuildingInstantRepairComplete,
  patchBuildingClearUpgradeCooldown,
  setLandStatus,
  setCastleBuildingStatus,
  setRepairRequirements,
  setRestoreBuilding,
  setSupportCharacters,
  setFetchSupportCharacters,
  setClearStatus,
  setSupportState,
  setUpgradeRequirements,
  setUpgradeBuilding,
  setUpgradeAvailable,
  setEmpirePeriod,
  enqueueRosterOp,
  confirmRosterOp,
  failRosterOp,
  setKingdomTechEffects,
  setRemoveCharacterFromBuilding,
  setAddCharacterToBuilding,
  setWellStart,
  setWellMiningStarted,
  setWellCheck,
  setTowerMiningCheck,
  setTowerMiningClaim,
  setTowerMiningStart,
  setTowerMiningStop,
  setTrainingCheck,
  setTrainingClaim,
  setTrainingStart,
  setImmediateTraining,
  setHealCharacter,
  setAllMiningStatus,
  patchAllMiningStatus,
  restoreAllMiningStatusEntry,
  patchBuildingStatusEntry,
} = landSlice.actions

export default landSlice.reducer
